<template>
  <div>
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>
    <div class="page-wrapper">
      <div class="content container-fluid">
        <div class="page-header">
          <div class="row align-items-center" style="width: -webkit-fill-available;">
            <div class="col-md-8">
              <h3 class="page-title mb-1">Gestion des sociétés</h3>
              <p class="text-muted mb-2">
                Gérez les inscriptions : création d'administrateur, activation ou mise à jour d'abonnement.
              </p>
              <ul class="nav nav-pills gap-2">
                <li class="nav-item">
                  <a
                    class="nav-link"
                    :class="{ active: isPendingView }"
                    href="javascript:void(0);"
                    @click.prevent="setActiveTab('pending')"
                  >En attente</a>
                </li>
                <li class="nav-item">
                  <a
                    class="nav-link"
                    :class="{ active: !isPendingView }"
                    href="javascript:void(0);"
                    @click.prevent="setActiveTab('all')"
                  >Toutes les sociétés</a>
                </li>
              </ul>
              <div class="row g-2 align-items-end mt-3 filter-row">
                <div class="col-md-6 col-lg-5">
                  <label class="form-label text-muted mb-1">Recherche</label>
                  <div class="input-group">
                    <span class="input-group-text bg-transparent"><i class="fas fa-search"></i></span>
                    <input
                      v-model="searchTerm"
                      type="text"
                      class="form-control"
                      placeholder="Nom, email, téléphone…"
                    />
                  </div>
                </div>
                <div class="col-md-4 col-lg-3">
                  <label class="form-label text-muted mb-1">Formule</label>
                  <select v-model="planFilter" class="form-select">
                    <option v-for="option in planOptions" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="col-md-4 d-flex justify-content-end gap-2">
              <button class="btn btn-outline-secondary" :disabled="isLoading" @click="refreshActiveTab">
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                <i class="fas fa-sync-alt me-2"></i>Rafraîchir
              </button>
              <button class="btn btn-primary" @click="openCreateModal">
                <i class="fas fa-building me-2"></i>Ajouter une société
              </button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div v-if="isLoading" class="text-center my-4">
              <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
            </div>

            <div v-else>
              <div v-if="displayedSocietes && displayedSocietes.length" class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Utilisateurs</th>
                      <th>Formule</th>
                      <th>Utilisateurs inclus</th>
                      <th>Administrateurs inclus</th>
                      <th>Date début</th>
                      <th>Date fin</th>
                      <th>Créé le</th>
                      <th class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="societe in pagedDisplayedSocietes" :key="societe.id">
                      <td>
                        <ul class="list-unstyled mb-0">
                          <li v-for="user in societe.users" :key="user.id">
                            <strong>{{ user.name }}</strong> — {{ user.email }}
                          </li>
                        </ul>
                      </td>
                      <td>
                        <span v-if="!getLatestAbonnement(societe)" class="text-muted">—</span>
                        <span v-else>{{ getLatestAbonnement(societe).nom }}</span>
                      </td>
                      <td>
                        <span v-if="!getLatestAbonnement(societe)" class="text-muted">—</span>
                        <span v-else>
                          <i class="fas fa-user-friends me-2 text-primary"></i>{{ getLatestAbonnement(societe).utilisateurs_inclus }}
                        </span>
                      </td>
                      <td>
                        <span v-if="!getLatestAbonnement(societe)" class="text-muted">—</span>
                        <span v-else>
                          <i class="fas fa-user-shield me-2 text-success"></i>{{ getLatestAbonnement(societe).administrateurs_inclus }}
                        </span>
                      </td>
                      <td>
                        <span v-if="!getLatestAbonnement(societe)" class="text-muted">—</span>
                        <span v-else>{{ formatDate(getLatestAbonnement(societe).date_debut) }}</span>
                      </td>
                      <td>
                        <span v-if="!getLatestAbonnement(societe)" class="text-muted">—</span>
                        <span v-else>
                          {{ formatDate(getLatestAbonnement(societe).date_fin) }}
                          <div class="text-muted small">
                            {{ formatRemainingDays(getLatestAbonnement(societe).date_fin) }}
                          </div>
                        </span>
                      </td>
                      <td>{{ formatDate(societe.created_at) }}</td>
                      <td class="text-center">
                        <div class="dropdown">
                          <button
                            class="btn btn-sm btn-outline-secondary dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            Actions
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end">
                            <li>
                              <button class="dropdown-item" type="button" @click="openAdminModal(societe)">
                                <i class="fas fa-user-plus me-2 text-primary"></i>Créer un administrateur
                              </button>
                            </li>
                            <li>
                              <button
                                class="dropdown-item"
                                type="button"
                                @click="openAbonnementModal(societe, getLatestAbonnement(societe))"
                              >
                                <i class="fas fa-id-badge me-2 text-success"></i>{{ getLatestAbonnement(societe) ? 'Modifier l\'abonnement' : 'Créer un abonnement' }}
                              </button>
                            </li>
                            <li>
                              <button class="dropdown-item" type="button" @click="openEditModal(societe)">
                                <i class="fas fa-edit me-2 text-warning"></i>Modifier la société
                              </button>
                            </li>
                            <li><hr class="dropdown-divider" /></li>
                            <li>
                              <button class="dropdown-item text-danger" type="button" @click="confirmDeleteSociete(societe)">
                                <i class="fas fa-trash-alt me-2"></i>Supprimer
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- pagination controls -->
              <nav v-if="displayedSocietes && displayedSocietes.length" class="mt-3">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-2">
                    <label class="small text-muted mb-0">Afficher</label>
                    <select class="form-select form-select-sm" v-model.number="pageSize" @change="changePageSize" style="width: auto;">
                      <option :value="10">10</option>
                      <option :value="25">25</option>
                      <option :value="50">50</option>
                    </select>
                    <span class="small text-muted"> / {{ totalItems }} éléments</span>
                  </div>

                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: page <= 1 }">
                      <button class="page-link" @click="goToPage(page - 1)" :disabled="page <= 1">Précédent</button>
                    </li>

                    <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: p === page }">
                      <button class="page-link" @click="goToPage(p)">{{ p }}</button>
                    </li>

                    <li class="page-item" :class="{ disabled: page >= totalPages }">
                      <button class="page-link" @click="goToPage(page + 1)" :disabled="page >= totalPages">Suivant</button>
                    </li>
                  </ul>
                </div>
              </nav>

              <div v-else class="text-center text-muted">
                Aucune société en attente.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Créer administrateur -->
  <div class="modal fade" ref="adminModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitAdminForm">
          <div class="modal-header">
            <h5 class="modal-title">Créer un administrateur</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="hideAdminModal"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">
              Société : <strong>{{ selectedSociete?.societe_nom }}</strong>
            </p>
            <div class="mb-3">
              <label class="form-label">Nom complet</label>
              <input v-model.trim="adminForm.name" type="text" class="form-control" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Email</label>
              <input v-model.trim="adminForm.email" type="email" class="form-control" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Mot de passe</label>
              <input v-model="adminForm.password" type="password" class="form-control" minlength="8" required />
              <div class="form-text">8 caractères minimum.</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="hideAdminModal" :disabled="adminSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="adminSubmitting">
              <span v-if="adminSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Créer abonnement -->
  <div class="modal fade" ref="abonnementModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitAbonnementForm">
          <div class="modal-header">
            <h5 class="modal-title">Créer un abonnement</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="hideAbonnementModal"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">
              Société : <strong>{{ selectedSociete?.societe_nom }}</strong>
            </p>
            <div class="mb-3">
              <label class="form-label">Formule</label>
              <select v-model="abonnementForm.nom" class="form-select" required>
                <option v-for="option in abonnementNomOptions" :key="option" :value="option">
                  {{ option }}
                </option>
              </select>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Utilisateurs inclus</label>
                <input v-model.number="abonnementForm.utilisateurs_inclus" type="number" class="form-control" min="0" required />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Administrateurs inclus</label>
                <input v-model.number="abonnementForm.administrateurs_inclus" type="number" class="form-control" min="0" required />
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Durée (mois)</label>
                <input
                  v-model.number="abonnementForm.duree_mois"
                  type="number"
                  class="form-control"
                  min="1"
                  @input="handleAbonnementDurationInput"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Date de début</label>
                <input v-model="abonnementForm.date_debut" type="date" class="form-control" />
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Date de fin (optionnelle)</label>
              <input
                v-model="abonnementForm.date_fin"
                type="date"
                class="form-control"
                @input="handleAbonnementDateFinInput"
              />
              <div class="form-text">Si vide, la durée sera utilisée pour calculer la date de fin.</div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="hideAbonnementModal" :disabled="abonnementSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-success" :disabled="abonnementSubmitting">
              <span v-if="abonnementSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              Valider
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Modifier société -->
  <div class="modal fade" ref="editModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitEditForm">
          <div class="modal-header">
            <h5 class="modal-title">Modifier la société</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="hideEditModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Nom</label>
              <input v-model.trim="editForm.societe_nom" type="text" class="form-control" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Email</label>
              <input v-model.trim="editForm.email" type="email" class="form-control" required />
            </div>
            <div class="mb-3">
              <label class="form-label">Téléphone</label>
              <input
                v-model.trim="editForm.telephone_entreprise"
                type="text"
                class="form-control"
                placeholder="ex. +212 6 12 34 56 78"
              />
            </div>
            <div class="mb-3">
              <label class="form-label">Adresse</label>
              <textarea
                v-model.trim="editForm.adress"
                class="form-control"
                rows="2"
                placeholder="Adresse du siège social"
              ></textarea>
            </div>
            <hr class="my-3" />
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="fw-semibold mb-0">Informations légales</h6>
              <span class="text-muted small">Mettez à jour les identifiants officiels si nécessaire</span>
            </div>
            <div class="row g-2">
              <div class="col-md-6">
                <label class="form-label">RC</label>
                <input
                  v-model.trim="editForm.rc"
                  type="text"
                  class="form-control"
                  placeholder="Registre du commerce"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">ICE</label>
                <input
                  v-model.trim="editForm.ice"
                  type="text"
                  class="form-control"
                  placeholder="Identifiant Commun de l'Entreprise"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">TP</label>
                <input
                  v-model.trim="editForm.tp"
                  type="text"
                  class="form-control"
                  placeholder="Taxe professionnelle"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Identifiant fiscal</label>
                <input
                  v-model.trim="editForm.identifiant_fiscal"
                  type="text"
                  class="form-control"
                  placeholder="IF"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">RIB</label>
                <input
                  v-model.trim="editForm.rib"
                  type="text"
                  class="form-control"
                  placeholder="Compte bancaire (RIB)"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label">Capital</label>
                <input
                  v-model.trim="editForm.capital"
                  type="text"
                  class="form-control"
                  placeholder="Capital social"
                />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="hideEditModal" :disabled="editSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-secondary" :disabled="editSubmitting">
              <span v-if="editSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Créer société -->
  <div class="modal fade" ref="createModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <form @submit.prevent="submitCreateForm">
          <div class="modal-header">
            <h5 class="modal-title">Ajouter une nouvelle société</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="hideCreateModal"></button>
          </div>
          <div class="modal-body">
            <h6 class="fw-semibold mb-3">Informations société</h6>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Nom</label>
                <input v-model.trim="createForm.societe_nom" type="text" class="form-control" required />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Email</label>
                <input v-model.trim="createForm.email" type="email" class="form-control" required />
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">Téléphone</label>
                <input
                  v-model.trim="createForm.telephone_entreprise"
                  type="text"
                  class="form-control"
                  placeholder="ex. +212 6 12 34 56 78"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">Adresse</label>
                <input
                  v-model.trim="createForm.adress"
                  type="text"
                  class="form-control"
                  placeholder="Adresse du siège social"
                />
              </div>
            </div>
            <hr class="my-4" />
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="fw-semibold mb-0">Informations légales</h6>
              <span class="text-muted small">Optionnel, mais recommandé pour un dossier complet</span>
            </div>
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">RC</label>
                <input
                  v-model.trim="createForm.rc"
                  type="text"
                  class="form-control"
                  placeholder="Registre du commerce"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">ICE</label>
                <input
                  v-model.trim="createForm.ice"
                  type="text"
                  class="form-control"
                  placeholder="Identifiant Commun de l'Entreprise"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">TP</label>
                <input
                  v-model.trim="createForm.tp"
                  type="text"
                  class="form-control"
                  placeholder="Taxe professionnelle"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Identifiant fiscal</label>
                <input
                  v-model.trim="createForm.identifiant_fiscal"
                  type="text"
                  class="form-control"
                  placeholder="IF"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">RIB</label>
                <input
                  v-model.trim="createForm.rib"
                  type="text"
                  class="form-control"
                  placeholder="Compte bancaire (RIB)"
                />
              </div>
              <div class="col-md-4">
                <label class="form-label">Capital</label>
                <input
                  v-model.trim="createForm.capital"
                  type="text"
                  class="form-control"
                  placeholder="Capital social"
                />
              </div>
            </div>

            <hr class="my-4" />
            <h6 class="fw-semibold mb-3">Administrateur</h6>
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Nom complet</label>
                <input v-model.trim="createForm.admin_name" type="text" class="form-control" required />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Email</label>
                <input v-model.trim="createForm.admin_email" type="email" class="form-control" required />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Mot de passe</label>
                <input v-model="createForm.admin_password" type="password" class="form-control" minlength="8" required />
              </div>
            </div>

            <hr class="my-4" />
            <div class="form-check form-switch mb-3">
              <input class="form-check-input" type="checkbox" id="createAbonnementSwitch" v-model="createForm.includeAbonnement" />
              <label class="form-check-label" for="createAbonnementSwitch">
                Créer immédiatement un abonnement
              </label>
            </div>

            <div v-if="createForm.includeAbonnement">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Formule</label>
                  <select v-model="createForm.abonnement_nom" class="form-select" required>
                    <option v-for="option in abonnementNomOptions" :key="option" :value="option">
                      {{ option }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Utilisateurs inclus</label>
                  <input v-model.number="createForm.utilisateurs_inclus" type="number" min="0" class="form-control" required />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Administrateurs inclus</label>
                  <input v-model.number="createForm.administrateurs_inclus" type="number" min="0" class="form-control" required />
                </div>
              </div>
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Durée (mois)</label>
                  <input
                    v-model.number="createForm.duree_mois"
                    type="number"
                    min="1"
                    class="form-control"
                    @input="handleCreateDurationInput"
                  />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Date de début</label>
                  <input v-model="createForm.date_debut" type="date" class="form-control" />
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Date de fin</label>
                  <input
                    v-model="createForm.date_fin"
                    type="date"
                    class="form-control"
                    @input="handleCreateDateFinInput"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" @click="hideCreateModal" :disabled="createSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="createSubmitting">
              <span v-if="createSubmitting" class="spinner-border spinner-border-sm me-1"></span>
              Créer la société
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, reactive, nextTick, computed, watch } from "vue";
import {
  fetchPendingSocietes,
  fetchAllSocietes,
  createSupportSociete,
  updateSupportSociete,
  deleteSupportSociete,
  createSupportSocieteAdmin,
  createSupportSocieteAbonnement,
  updateSupportSocieteAbonnement,
  fetchSupportAbonnementPlans,
} from "@/services/devSupportService";
import { toast } from "vue3-toastify";
import { Modal } from "bootstrap";

const loadingPending = ref(false);
const loadingAll = ref(false);
const pendingSocietes = ref([]);
const allSocietes = ref([]);
const activeTab = ref("pending");
const searchTerm = ref("");
const planFilter = ref("ALL");

const DEFAULT_PLAN_LIST = [
  { nom: "INTILAK", utilisateurs_inclus: 2, administrateurs_inclus: 1, duree_mois: 12 },
  { nom: "IMTYAZ", utilisateurs_inclus: 5, administrateurs_inclus: 2, duree_mois: 12 },
  { nom: "IRTYAH", utilisateurs_inclus: 10, administrateurs_inclus: 3, duree_mois: 12 },
];

const DEFAULT_PLAN_LOOKUP = DEFAULT_PLAN_LIST.reduce((acc, plan) => {
  acc[plan.nom] = plan;
  return acc;
}, {});

const planList = ref([...DEFAULT_PLAN_LIST]);

const planMap = computed(() =>
  planList.value.reduce((acc, plan) => {
    acc[plan.nom] = plan;
    return acc;
  }, {})
);

const planOptions = computed(() => [
  { value: "ALL", label: "Toutes les formules" },
  { value: "NONE", label: "Sans abonnement" },
  ...planList.value.map((plan) => ({
    value: plan.nom,
    label: plan.nom,
  })),
]);

const abonnementNomOptions = computed(() => planList.value.map((plan) => plan.nom));

const toNumberOr = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? fallback : numeric;
};

const getDefaultPlan = () =>
  planList.value[0] ||
  DEFAULT_PLAN_LIST[0] || {
    nom: "",
    utilisateurs_inclus: 0,
    administrateurs_inclus: 0,
    duree_mois: 12,
  };

const isLoading = computed(() =>
  activeTab.value === "pending" ? loadingPending.value : loadingAll.value
);
const displayedSocietes = computed(() => {
  const baseList = activeTab.value === "pending" ? pendingSocietes.value : allSocietes.value;
  const term = searchTerm.value.trim().toLowerCase();
  const plan = planFilter.value;

  return baseList.filter((societe) => {
    const abonnement = getLatestAbonnement(societe);

    let matchesPlan = true;
    if (plan === "NONE") {
      matchesPlan = !abonnement;
    } else if (plan !== "ALL") {
      matchesPlan = abonnement && abonnement.nom === plan;
    }

    let matchesTerm = true;
    if (term) {
      const haystack = [
        societe.societe_nom,
        societe.email,
        societe.telephone_entreprise,
        societe.adress,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      matchesTerm = haystack.includes(term);
    }

    return matchesPlan && matchesTerm;
  });
});
const isPendingView = computed(() => activeTab.value === "pending");
const selectedSociete = ref(null);
const REFRESH_INTERVAL_MS = 20000;
let autoRefreshTimer = null;
let suppressAbonnementPlanWatcher = false;

const adminForm = reactive({
  name: "",
  email: "",
  password: "",
});
const adminSubmitting = ref(false);

const initialPlan =
  planList.value[0] ||
  DEFAULT_PLAN_LIST[0] || {
    nom: "",
    utilisateurs_inclus: 0,
    administrateurs_inclus: 0,
    duree_mois: 12,
  };

const abonnementForm = reactive({
  nom: initialPlan.nom,
  utilisateurs_inclus: toNumberOr(initialPlan.utilisateurs_inclus, 0),
  administrateurs_inclus: toNumberOr(initialPlan.administrateurs_inclus, 0),
  duree_mois: toNumberOr(initialPlan.duree_mois, 12),
  date_debut: "",
  date_fin: "",
});
const abonnementSubmitting = ref(false);
const isUpdatingAbonnement = ref(false);
const selectedAbonnementId = ref(null);

const editForm = reactive({
  societe_nom: "",
  email: "",
  telephone_entreprise: "",
  adress: "",
  rc: "",
  ice: "",
  tp: "",
  identifiant_fiscal: "",
  rib: "",
  capital: "",
});
const editSubmitting = ref(false);

const createForm = reactive({
  societe_nom: "",
  email: "",
  telephone_entreprise: "",
  adress: "",
  rc: "",
  ice: "",
  tp: "",
  identifiant_fiscal: "",
  rib: "",
  capital: "",
  admin_name: "",
  admin_email: "",
  admin_password: "",
  includeAbonnement: false,
  abonnement_nom: initialPlan.nom,
  utilisateurs_inclus: toNumberOr(initialPlan.utilisateurs_inclus, 0),
  administrateurs_inclus: toNumberOr(initialPlan.administrateurs_inclus, 0),
  duree_mois: toNumberOr(initialPlan.duree_mois, 12),
  date_debut: "",
  date_fin: "",
});
const createSubmitting = ref(false);

const autoCalcAbonnementDateFin = ref(true);
const autoCalcCreateDateFin = ref(true);

const adminModalRef = ref(null);
const abonnementModalRef = ref(null);
const editModalRef = ref(null);
const createModalRef = ref(null);

let adminModalInstance = null;
let abonnementModalInstance = null;
let editModalInstance = null;
let createModalInstance = null;

const resetAdminForm = () => {
  adminForm.name = "";
  adminForm.email = "";
  adminForm.password = "";
};

const resetAbonnementForm = () => {
  const plan = getDefaultPlan();
  autoCalcAbonnementDateFin.value = true;
  suppressAbonnementPlanWatcher = true;
  abonnementForm.nom = plan.nom;
  abonnementForm.utilisateurs_inclus = toNumberOr(plan.utilisateurs_inclus, 0);
  abonnementForm.administrateurs_inclus = toNumberOr(plan.administrateurs_inclus, 0);
  abonnementForm.duree_mois = toNumberOr(plan.duree_mois, 12);
  abonnementForm.date_debut = "";
  abonnementForm.date_fin = "";
  suppressAbonnementPlanWatcher = false;
};

const resetCreateForm = () => {
  autoCalcCreateDateFin.value = true;
  createForm.societe_nom = "";
  createForm.email = "";
  createForm.telephone_entreprise = "";
  createForm.adress = "";
  createForm.rc = "";
  createForm.ice = "";
  createForm.tp = "";
  createForm.identifiant_fiscal = "";
  createForm.rib = "";
  createForm.capital = "";
  createForm.admin_name = "";
  createForm.admin_email = "";
  createForm.admin_password = "";
  createForm.includeAbonnement = false;
  const plan = getDefaultPlan();
  createForm.abonnement_nom = plan.nom;
  createForm.utilisateurs_inclus = toNumberOr(plan.utilisateurs_inclus, 0);
  createForm.administrateurs_inclus = toNumberOr(plan.administrateurs_inclus, 0);
  createForm.duree_mois = toNumberOr(plan.duree_mois, 12);
  createForm.date_debut = "";
  createForm.date_fin = "";
};

const getPlanPreset = (planNom) => planMap.value[planNom] || DEFAULT_PLAN_LOOKUP[planNom] || null;

const ensurePlanFilterIsValid = () => {
  if (planFilter.value === "ALL" || planFilter.value === "NONE") return;
  if (!getPlanPreset(planFilter.value)) {
    planFilter.value = "ALL";
  }
};

const applyPlanDefaultsToAbonnementForm = (planNom) => {
  const preset = getPlanPreset(planNom);
  if (!preset) return;
  abonnementForm.utilisateurs_inclus = toNumberOr(
    preset.utilisateurs_inclus,
    abonnementForm.utilisateurs_inclus ?? 0
  );
  abonnementForm.administrateurs_inclus = toNumberOr(
    preset.administrateurs_inclus,
    abonnementForm.administrateurs_inclus ?? 0
  );
  abonnementForm.duree_mois = toNumberOr(preset.duree_mois, abonnementForm.duree_mois ?? 12);
};

const applyPlanDefaultsToCreateForm = (planNom) => {
  const preset = getPlanPreset(planNom);
  if (!preset) return;
  createForm.utilisateurs_inclus = toNumberOr(
    preset.utilisateurs_inclus,
    createForm.utilisateurs_inclus ?? 0
  );
  createForm.administrateurs_inclus = toNumberOr(
    preset.administrateurs_inclus,
    createForm.administrateurs_inclus ?? 0
  );
  createForm.duree_mois = toNumberOr(preset.duree_mois, createForm.duree_mois ?? 12);
};

const calculateEndDate = (startDate, durationMonths) => {
  if (!startDate) return "";
  const months = Number(durationMonths);
  if (!Number.isFinite(months) || months <= 0) return "";
  const [yearStr, monthStr, dayStr] = startDate.split("-");
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;
  const day = Number(dayStr);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(monthIndex) ||
    !Number.isFinite(day) ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return "";
  }
  const base = new Date(Date.UTC(year, monthIndex, day));
  if (Number.isNaN(base.getTime())) return "";
  base.setUTCMonth(base.getUTCMonth() + months);
  const computedYear = base.getUTCFullYear();
  const computedMonth = String(base.getUTCMonth() + 1).padStart(2, "0");
  const computedDay = String(base.getUTCDate()).padStart(2, "0");
  return `${computedYear}-${computedMonth}-${computedDay}`;
};

const updateAbonnementDateFin = () => {
  if (!autoCalcAbonnementDateFin.value) return;
  if (!abonnementForm.date_debut || !abonnementForm.duree_mois) {
    abonnementForm.date_fin = "";
    return;
  }
  const computed = calculateEndDate(abonnementForm.date_debut, abonnementForm.duree_mois);
  abonnementForm.date_fin = computed || "";
};

const updateCreateDateFin = () => {
  if (!createForm.includeAbonnement) {
    createForm.date_fin = "";
    return;
  }
  if (!autoCalcCreateDateFin.value) return;
  if (!createForm.date_debut || !createForm.duree_mois) {
    createForm.date_fin = "";
    return;
  }
  const computed = calculateEndDate(createForm.date_debut, createForm.duree_mois);
  createForm.date_fin = computed || "";
};

const handleAbonnementDateFinInput = (event) => {
  const value = event?.target?.value ?? "";
  autoCalcAbonnementDateFin.value = value === "";
  if (autoCalcAbonnementDateFin.value) {
    updateAbonnementDateFin();
  }
};

const handleCreateDateFinInput = (event) => {
  const value = event?.target?.value ?? "";
  autoCalcCreateDateFin.value = value === "";
  if (autoCalcCreateDateFin.value) {
    updateCreateDateFin();
  }
};

const handleAbonnementDurationInput = () => {
  autoCalcAbonnementDateFin.value = true;
  updateAbonnementDateFin();
};

const handleCreateDurationInput = () => {
  autoCalcCreateDateFin.value = true;
  updateCreateDateFin();
};

watch(
  () => abonnementForm.nom,
  (newNom) => {
    if (!newNom || suppressAbonnementPlanWatcher) return;
    applyPlanDefaultsToAbonnementForm(newNom);
    updateAbonnementDateFin();
  }
);

watch(
  () => createForm.abonnement_nom,
  (newNom) => {
    if (!newNom || !createForm.includeAbonnement) return;
    applyPlanDefaultsToCreateForm(newNom);
    updateCreateDateFin();
  }
);

watch(
  () => createForm.includeAbonnement,
  (enabled) => {
    if (enabled) {
      autoCalcCreateDateFin.value = true;
      applyPlanDefaultsToCreateForm(createForm.abonnement_nom);
      updateCreateDateFin();
    } else {
      autoCalcCreateDateFin.value = true;
      createForm.date_fin = "";
    }
  }
);

watch(
  [() => abonnementForm.duree_mois, () => abonnementForm.date_debut, () => autoCalcAbonnementDateFin.value],
  () => {
    updateAbonnementDateFin();
  },
  { immediate: true }
);

watch(
  [
    () => createForm.duree_mois,
    () => createForm.date_debut,
    () => autoCalcCreateDateFin.value,
    () => createForm.includeAbonnement,
  ],
  () => {
    updateCreateDateFin();
  },
  { immediate: true }
);

const loadAbonnementPlans = async () => {
  try {
    const response = await fetchSupportAbonnementPlans();
    const plans = response?.data || [];
    if (!Array.isArray(plans) || plans.length === 0) {
      ensurePlanFilterIsValid();
      return;
    }

    const normalizedPlans = plans
      .filter((plan) => plan && typeof plan.nom === "string" && plan.nom.length > 0)
      .map((plan) => {
        const fallback = DEFAULT_PLAN_LOOKUP[plan.nom] || getDefaultPlan();
        return {
          nom: plan.nom,
          utilisateurs_inclus: toNumberOr(
            plan.utilisateurs_inclus,
            fallback ? fallback.utilisateurs_inclus ?? 0 : 0
          ),
          administrateurs_inclus: toNumberOr(
            plan.administrateurs_inclus,
            fallback ? fallback.administrateurs_inclus ?? 0 : 0
          ),
          duree_mois: toNumberOr(plan.duree_mois, fallback ? fallback.duree_mois ?? 12 : 12),
        };
      });

    if (normalizedPlans.length === 0) {
      ensurePlanFilterIsValid();
      return;
    }

    planList.value = normalizedPlans;
    ensurePlanFilterIsValid();

    if (!getPlanPreset(abonnementForm.nom)) {
      const fallbackPlan = getDefaultPlan();
      suppressAbonnementPlanWatcher = true;
      abonnementForm.nom = fallbackPlan.nom;
      applyPlanDefaultsToAbonnementForm(fallbackPlan.nom);
      suppressAbonnementPlanWatcher = false;
    } else {
      applyPlanDefaultsToAbonnementForm(abonnementForm.nom);
    }
    updateAbonnementDateFin();

    if (!getPlanPreset(createForm.abonnement_nom) && planList.value.length > 0) {
      const fallbackPlan = getDefaultPlan();
      createForm.abonnement_nom = fallbackPlan.nom;
      applyPlanDefaultsToCreateForm(fallbackPlan.nom);
    } else if (createForm.includeAbonnement) {
      applyPlanDefaultsToCreateForm(createForm.abonnement_nom);
    }
    updateCreateDateFin();
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Impossible de charger les formules d'abonnement.";
    toast.error(message);
  }
};

const loadPendingSocietes = async (showLoader = true) => {
  if (showLoader) loadingPending.value = true;
  try {
    const response = await fetchPendingSocietes();
    pendingSocietes.value = response?.data || [];
  } catch (error) {
    const message = error?.response?.data?.message || "Impossible de charger les sociétés.";
    toast.error(message);
  } finally {
    if (showLoader) loadingPending.value = false;
  }
};

const loadAllSocietes = async (showLoader = true) => {
  if (showLoader) loadingAll.value = true;
  try {
    const response = await fetchAllSocietes();
    allSocietes.value = response?.data || [];
  } catch (error) {
    const message = error?.response?.data?.message || "Impossible de charger les sociétés.";
    toast.error(message);
  } finally {
    if (showLoader) loadingAll.value = false;
  }
};

const refreshLists = async () => {
  await Promise.all([
    loadPendingSocietes(activeTab.value === "pending"),
    loadAllSocietes(activeTab.value === "all"),
    loadAbonnementPlans(),
  ]);
};

const refreshActiveTab = async () => {
  if (activeTab.value === "pending") {
    await Promise.all([loadPendingSocietes(), loadAbonnementPlans()]);
  } else {
    await Promise.all([loadAllSocietes(), loadAbonnementPlans()]);
  }
};

const setActiveTab = (tab) => {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  if (tab === "all" && allSocietes.value.length === 0) {
    loadAllSocietes();
  } else if (tab === "pending" && pendingSocietes.value.length === 0) {
    loadPendingSocietes();
  }
  startAutoRefresh();
};

const startAutoRefresh = () => {
  stopAutoRefresh();
  autoRefreshTimer = window.setInterval(() => {
    loadPendingSocietes(false);
    loadAllSocietes(false);
  }, REFRESH_INTERVAL_MS);
};

const stopAutoRefresh = () => {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
};

const openAdminModal = (societe) => {
  selectedSociete.value = societe;
  resetAdminForm();
  adminModalInstance?.show();
};

const hideAdminModal = () => {
  adminModalInstance?.hide();
};

const submitAdminForm = async () => {
  if (!selectedSociete.value) return;
  adminSubmitting.value = true;
  try {
    await createSupportSocieteAdmin(selectedSociete.value.id, { ...adminForm });
    toast.success("Administrateur créé avec succès.");
    hideAdminModal();
    await refreshLists();
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Impossible de créer l'administrateur.";
    toast.error(message);
  } finally {
    adminSubmitting.value = false;
  }
};

const openAbonnementModal = (societe, abonnement = null) => {
  selectedSociete.value = societe;
  if (abonnement) {
    if (abonnement.nom && !getPlanPreset(abonnement.nom)) {
      planList.value = [
        ...planList.value,
        {
          nom: abonnement.nom,
          utilisateurs_inclus: toNumberOr(abonnement.utilisateurs_inclus, 0),
          administrateurs_inclus: toNumberOr(abonnement.administrateurs_inclus, 0),
          duree_mois: toNumberOr(
            abonnement.duree_mois,
            getDefaultPlan().duree_mois ?? 12
          ),
        },
      ];
    }

    isUpdatingAbonnement.value = true;
    selectedAbonnementId.value = abonnement.id;
    suppressAbonnementPlanWatcher = true;
    const planName = abonnement.nom ?? getDefaultPlan().nom;
    const preset = getPlanPreset(planName);
    abonnementForm.nom = planName;
    abonnementForm.utilisateurs_inclus = toNumberOr(
      abonnement.utilisateurs_inclus,
      preset ? preset.utilisateurs_inclus ?? 0 : abonnementForm.utilisateurs_inclus ?? 0
    );
    abonnementForm.administrateurs_inclus = toNumberOr(
      abonnement.administrateurs_inclus,
      preset ? preset.administrateurs_inclus ?? 0 : abonnementForm.administrateurs_inclus ?? 0
    );
    if (abonnement.duree_mois !== null && abonnement.duree_mois !== undefined) {
      abonnementForm.duree_mois = toNumberOr(
        abonnement.duree_mois,
        preset ? preset.duree_mois ?? 12 : abonnementForm.duree_mois ?? 12
      );
    } else if (preset) {
      abonnementForm.duree_mois = toNumberOr(preset.duree_mois, abonnementForm.duree_mois ?? 12);
    }
    abonnementForm.date_debut = abonnement.date_debut ? abonnement.date_debut.slice(0, 10) : "";
    abonnementForm.date_fin = abonnement.date_fin ? abonnement.date_fin.slice(0, 10) : "";
    suppressAbonnementPlanWatcher = false;
    autoCalcAbonnementDateFin.value = !abonnementForm.date_fin;
    updateAbonnementDateFin();
  } else {
    isUpdatingAbonnement.value = false;
    selectedAbonnementId.value = null;
    resetAbonnementForm();
  }
  abonnementModalInstance?.show();
};

const hideAbonnementModal = () => {
  abonnementModalInstance?.hide();
  isUpdatingAbonnement.value = false;
  selectedAbonnementId.value = null;
  resetAbonnementForm();
};

const submitAbonnementForm = async () => {
  if (!selectedSociete.value) return;
  abonnementSubmitting.value = true;
  try {
    const payload = { ...abonnementForm };
    if (payload.date_debut === "") payload.date_debut = null;
    if (payload.date_fin === "") payload.date_fin = null;
    let message = "Abonnement créé avec succès.";
    if (isUpdatingAbonnement.value && selectedAbonnementId.value) {
      await updateSupportSocieteAbonnement(
        selectedSociete.value.id,
        selectedAbonnementId.value,
        payload
      );
      message = "Abonnement mis à jour avec succès.";
    } else {
      await createSupportSocieteAbonnement(selectedSociete.value.id, payload);
    }
    toast.success(message);
    hideAbonnementModal();
    await refreshLists();
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Impossible d'enregistrer l'abonnement.";
    toast.error(message);
  } finally {
    abonnementSubmitting.value = false;
  }
};

const openEditModal = (societe) => {
  selectedSociete.value = societe;
  editForm.societe_nom = societe.societe_nom || "";
  editForm.email = societe.email || "";
  editForm.telephone_entreprise = societe.telephone_entreprise || "";
  editForm.adress = societe.adress || "";
  editForm.rc = societe.rc || "";
  editForm.ice = societe.ice || "";
  editForm.tp = societe.tp || "";
  editForm.identifiant_fiscal = societe.identifiant_fiscal || "";
  editForm.rib = societe.rib || "";
  editForm.capital = societe.capital || "";
  editModalInstance?.show();
};

const hideEditModal = () => {
  editModalInstance?.hide();
};

const submitEditForm = async () => {
  if (!selectedSociete.value) return;
  editSubmitting.value = true;
  try {
    await updateSupportSociete(selectedSociete.value.id, { ...editForm });
    toast.success("Société mise à jour avec succès.");
    hideEditModal();
    await refreshLists();
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Impossible de mettre à jour la société.";
    toast.error(message);
  } finally {
    editSubmitting.value = false;
  }
};

const confirmDeleteSociete = async (societe) => {
  if (!societe) return;
  const confirmed = window.confirm(`Supprimer la société "${societe.societe_nom}" ? Cette action est irréversible.`);
  if (!confirmed) return;

  try {
    await deleteSupportSociete(societe.id);
    toast.success("Société supprimée avec succès.");
    await refreshLists();
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Impossible de supprimer la société.";
    toast.error(message);
  }
};

const openCreateModal = () => {
  resetCreateForm();
  createModalInstance?.show();
};

const hideCreateModal = () => {
  createModalInstance?.hide();
};

const submitCreateForm = async () => {
  if (!createForm.societe_nom || !createForm.email || !createForm.telephone_entreprise) {
    toast.error("Veuillez renseigner les informations de la société.");
    return;
  }
  if (!createForm.admin_name || !createForm.admin_email || !createForm.admin_password) {
    toast.error("Veuillez renseigner les informations de l'administrateur.");
    return;
  }

  const payload = {
    societe: {
      societe_nom: createForm.societe_nom,
      email: createForm.email,
      telephone_entreprise: createForm.telephone_entreprise,
      adress: createForm.adress,
      rc: createForm.rc,
      ice: createForm.ice,
      tp: createForm.tp,
      identifiant_fiscal: createForm.identifiant_fiscal,
      rib: createForm.rib,
      capital: createForm.capital,
    },
    admin: {
      name: createForm.admin_name,
      email: createForm.admin_email,
      password: createForm.admin_password,
    },
  };

  if (createForm.includeAbonnement) {
    payload.abonnement = {
      nom: createForm.abonnement_nom,
      utilisateurs_inclus: createForm.utilisateurs_inclus,
      administrateurs_inclus: createForm.administrateurs_inclus,
      duree_mois: createForm.duree_mois,
      date_debut: createForm.date_debut,
      date_fin: createForm.date_fin,
    };
    if (payload.abonnement.date_debut === "") payload.abonnement.date_debut = null;
    if (payload.abonnement.date_fin === "") payload.abonnement.date_fin = null;
  }

  createSubmitting.value = true;
  try {
    await createSupportSociete(payload);
    toast.success("Société créée avec succès.");
    hideCreateModal();
    await refreshLists();
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Impossible de créer la société.";
    toast.error(message);
  } finally {
    createSubmitting.value = false;
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getLatestAbonnement = (societe) => {
  if (!societe || !Array.isArray(societe.abonnements) || societe.abonnements.length === 0) {
    return null;
  }
  return societe.abonnements[0];
};

const formatRemainingDays = (dateFin) => {
  if (!dateFin) return "—";
  const end = new Date(dateFin);
  if (Number.isNaN(end.getTime())) return "—";
  const normalize = (d) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };
  const diffMs = normalize(end) - normalize(new Date());
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays > 0) {
    return `${diffDays} jour${diffDays > 1 ? 's' : ''} restants`;
  }
  if (diffDays === 0) {
    return "Expire aujourd'hui";
  }
  const abs = Math.abs(diffDays);
  return `Expiré depuis ${abs} jour${abs > 1 ? 's' : ''}`;
};

const page = ref(1);
const pageSize = ref(10);

const totalItems = computed(() => displayedSocietes.value.length);
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)));

// windowed visible pages for compact UI
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = Number(page.value || 1);
  const maxVisible = 7;
  if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  let end = start + maxVisible - 1;
  if (end > total) {
    end = total;
    start = end - maxVisible + 1;
  }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

const pagedDisplayedSocietes = computed(() => {
  const list = displayedSocietes.value || [];
  const start = (page.value - 1) * pageSize.value;
  return list.slice(start, start + pageSize.value);
});

const goToPage = (p) => {
  const target = Number(p);
  if (!Number.isFinite(target)) return;
  if (target < 1 || target > totalPages.value) return;
  page.value = target;
  // optional: scroll into view of the table
  // document.querySelector('.table-responsive')?.scrollIntoView({ behavior: 'smooth' });
};

const changePageSize = () => {
  page.value = 1;
};

// reset page when filtered list changes
watch([displayedSocietes, pageSize], () => {
  if (page.value > totalPages.value) {
    page.value = 1;
  }
});

onMounted(async () => {
  await loadAbonnementPlans();
  await loadPendingSocietes();
  loadAllSocietes(false);
  nextTick(() => {
    if (adminModalRef.value) {
      adminModalInstance = new Modal(adminModalRef.value, { backdrop: "static" });
    }
    if (abonnementModalRef.value) {
      abonnementModalInstance = new Modal(abonnementModalRef.value, { backdrop: "static" });
    }
    if (editModalRef.value) {
      editModalInstance = new Modal(editModalRef.value, { backdrop: "static" });
    }
    if (createModalRef.value) {
      createModalInstance = new Modal(createModalRef.value, { backdrop: "static" });
    }
  });
  startAutoRefresh();
});

onBeforeUnmount(() => {
  stopAutoRefresh();
});
</script>

<style scoped>
.support-header-col {
  width: -webkit-fill-available;
}
.filter-row .form-control,
.filter-row .form-select {
  border-radius: 8px;
}
.company-logo {
  width: 48px;
  height: 48px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.company-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.placeholder-logo {
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.5px;
}
.btn-group .btn {
  min-width: 120px;
}
</style>
